import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  BookOpen,
  FileSpreadsheet,
  FileText,
  Film,
  Layers,
  Package,
  Play,
  Presentation,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import { openUrlInNewTab } from '@/config/ipeRoutes';
import PlatformTutorialPreviewModal from '@/components/platform-tutorial/PlatformTutorialPreviewModal';
import { platformTutorialAPI } from '@/utils/api';
import { IPE_SHEIN_GATE_PATH } from '@/shein-platform/constants/sheinRoutes';
import { IPE_TEMU_GATE_PATH } from '@/temu-platform/constants/temuRoutes';
import { IPE_TIKTOK_GATE_PATH } from '@/tiktok-platform/constants/tiktokRoutes';
import { showToast } from '@/utils/toast';
import '@/styles/ipe/commerce/CommercePlatformPickerPage.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const SHEIN_CARD_BG = `${PUBLIC_URL}/images/commerce/commerce-picker-shein.jpg`;
const TEMU_CARD_BG = `${PUBLIC_URL}/images/commerce/commerce-picker-temu.jpg`;
const TIKTOK_CARD_BG = `${PUBLIC_URL}/images/commerce/commerce-picker-tiktok.jpg?v=2`;

/** 临时关闭 TEMU 入口；恢复时改为 true 即可 */
const TEMU_WORKSPACE_ENABLED = true;
const TIKTOK_WORKSPACE_ENABLED = true;

const FEATURES = [
  { id: 'workspace', icon: Layers, titleKey: 'featureWorkspaceTitle', descKey: 'featureWorkspaceDesc' },
  { id: 'listing', icon: Package, titleKey: 'featureListingTitle', descKey: 'featureListingDesc' },
  { id: 'compliance', icon: ShieldCheck, titleKey: 'featureComplianceTitle', descKey: 'featureComplianceDesc' },
  { id: 'orders', icon: ShoppingBag, titleKey: 'featureOrdersTitle', descKey: 'featureOrdersDesc' },
];

function tutorialTypeOf(item) {
  return String(item?.tutorialType || item?.type || '').toUpperCase() === 'DOCUMENT'
    ? 'DOCUMENT'
    : 'VIDEO';
}

/** 未上传封面时，按扩展名 / MIME 选文件类型图标 */
function documentCoverMeta(item, t) {
  const name = `${item?.fileName || ''} ${item?.viewUrl || ''} ${item?.fileKey || ''}`.toLowerCase();
  const mime = String(item?.mimeType || '').toLowerCase();
  if (mime.includes('pdf') || /\.pdf(\?|#|$)/i.test(name)) {
    return { Icon: FileText, label: 'PDF', tone: 'pdf' };
  }
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv'
    || /\.(xlsx|xls|csv)(\?|#|$)/i.test(name)) {
    return { Icon: FileSpreadsheet, label: t('commerce.fileSheet'), tone: 'sheet' };
  }
  if (mime.includes('presentation') || /\.(pptx|ppt)(\?|#|$)/i.test(name)) {
    return { Icon: Presentation, label: t('commerce.filePpt'), tone: 'ppt' };
  }
  if (mime.includes('word') || /\.(docx|doc)(\?|#|$)/i.test(name)) {
    return { Icon: FileText, label: 'Word', tone: 'doc' };
  }
  if (mime.includes('markdown') || /\.(md|markdown)(\?|#|$)/i.test(name)) {
    return { Icon: BookOpen, label: 'MD', tone: 'md' };
  }
  return { Icon: FileText, label: t('commerce.fileDoc'), tone: 'file' };
}

function useRevealOnScroll(rootRef, readyKey) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const scrollRoot = root.querySelector('.commerce-picker__scroll');
    const nodes = root.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (nodes.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: scrollRoot || null, threshold: 0.14, rootMargin: '0px 0px -6% 0px' },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [rootRef, readyKey]);
}

/**
 * @param {{ embedded?: boolean }} [props]
 * embedded：工作台右侧内嵌（保留左侧栏）；完整页用于 /ipe/commerce 直达。
 */
export default function CommercePlatformPickerPage({ embedded = false }) {
  const { t } = useTranslation('workbench');
  const pageRef = useRef(null);
  const featuresRef = useRef(null);
  const platformsRef = useRef(null);
  const videosRef = useRef(null);
  const docsRef = useRef(null);
  const [tutorials, setTutorials] = useState([]);
  const [tutorialsLoading, setTutorialsLoading] = useState(true);
  const [tutorialsError, setTutorialsError] = useState('');
  const [viewer, setViewer] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const videoPlayerRef = useRef(null);
  const autoPlayVideoRef = useRef(false);

  useRevealOnScroll(pageRef, `${tutorialsLoading}:${tutorialsError}:${tutorials.length}`);

  const openGate = (path) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    openUrlInNewTab(`${origin}${path}`);
  };

  const openTemuGate = () => {
    if (!TEMU_WORKSPACE_ENABLED) {
      showToast(t('commerce.temuComing'), 'info');
      return;
    }
    openGate(IPE_TEMU_GATE_PATH);
  };

  const openTiktokGate = () => {
    if (!TIKTOK_WORKSPACE_ENABLED) {
      showToast(t('commerce.tiktokComing'), 'info');
      return;
    }
    openGate(IPE_TIKTOK_GATE_PATH);
  };

  const loadTutorials = useCallback(() => {
    setTutorialsLoading(true);
    setTutorialsError('');
    platformTutorialAPI.list()
      .then((res) => setTutorials(Array.isArray(res?.items) ? res.items : []))
      .catch((e) => {
        setTutorials([]);
        setTutorialsError(e?.message || t('commerce.tutorialLoadFailed'));
      })
      .finally(() => setTutorialsLoading(false));
  }, [t]);

  useEffect(() => {
    loadTutorials();
  }, [loadTutorials]);

  useEffect(() => {
    let target = '';
    try {
      target = sessionStorage.getItem('ipe.commerce.scrollTo') || '';
      if (target) sessionStorage.removeItem('ipe.commerce.scrollTo');
    } catch {
      target = '';
    }
    if (!target && typeof window !== 'undefined') {
      const hash = String(window.location.hash || '').replace(/^#/, '');
      if (hash === 'videos' || hash === 'tutorials') target = 'videos';
      if (hash === 'docs' || hash === 'documents') target = 'docs';
    }
    if (target !== 'videos' && target !== 'docs') return undefined;
    const ref = target === 'docs' ? docsRef : videosRef;
    const timer = window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, []);

  const videoTutorials = useMemo(
    () => tutorials.filter((item) => tutorialTypeOf(item) === 'VIDEO'),
    [tutorials],
  );
  const documentTutorials = useMemo(
    () => tutorials.filter((item) => tutorialTypeOf(item) === 'DOCUMENT'),
    [tutorials],
  );

  useEffect(() => {
    if (videoTutorials.length === 0) {
      setActiveVideoId(null);
      return;
    }
    setActiveVideoId((prev) => {
      if (prev && videoTutorials.some((item) => item.id === prev)) return prev;
      return videoTutorials[0].id;
    });
  }, [videoTutorials]);

  const activeVideo = useMemo(
    () => videoTutorials.find((item) => item.id === activeVideoId) || null,
    [videoTutorials, activeVideoId],
  );

  const activeVideoUrl = String(activeVideo?.viewUrl || '').trim();

  const scrollTo = (ref) => {
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const selectVideo = (item, { autoPlay = true } = {}) => {
    if (!item?.id) return;
    autoPlayVideoRef.current = Boolean(autoPlay);
    setActiveVideoId(item.id);
  };

  const handleVideoReady = (event) => {
    if (!autoPlayVideoRef.current) return;
    autoPlayVideoRef.current = false;
    event.currentTarget.play?.().catch(() => {
      /* 浏览器可能拦截自动播放，用户可手动点播放 */
    });
  };

  const openDocument = (item) => {
    const url = String(item?.viewUrl || '').trim();
    if (!url) return;
    setViewer({
      title: item.title || t('commerce.tutorialDoc'),
      url,
      type: 'DOCUMENT',
      mimeType: item.mimeType || '',
      fileName: item.fileName || '',
    });
  };

  const renderDocumentGrid = (items, emptyLabel) => {
    if (tutorialsLoading) {
      return <div className="commerce-picker__empty">{t('commerce.loading')}</div>;
    }
    if (tutorialsError) {
      return (
        <div className="commerce-picker__empty">
          <span>{tutorialsError}</span>
          <button type="button" className="commerce-picker__text-btn" onClick={loadTutorials}>{t('commerce.retry')}</button>
        </div>
      );
    }
    if (items.length === 0) {
      return <div className="commerce-picker__empty">{emptyLabel}</div>;
    }
    return (
      <div className="commerce-picker__tutorial-grid">
        {items.map((item) => {
          const coverUrl = String(item.coverUrl || '').trim();
          const { Icon, label, tone } = documentCoverMeta(item, t);
          return (
            <button
              key={item.id}
              type="button"
              className="commerce-picker__tutorial-card"
              onClick={() => openDocument(item)}
            >
              <div
                className={`commerce-picker__tutorial-cover${coverUrl ? '' : ` commerce-picker__tutorial-cover--file commerce-picker__tutorial-cover--${tone}`}`}
                style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
              >
                {coverUrl ? null : (
                  <span className="commerce-picker__file-badge" aria-hidden="true">
                    <Icon size={40} strokeWidth={1.4} />
                  </span>
                )}
                <span>{label}</span>
              </div>
              <div className="commerce-picker__tutorial-body">
                <strong>{item.title || t('commerce.untitled')}</strong>
                {item.description ? <span>{item.description}</span> : null}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderVideoSectionBody = () => {
    if (tutorialsLoading) {
      return <div className="commerce-picker__empty">{t('commerce.loading')}</div>;
    }
    if (tutorialsError) {
      return (
        <div className="commerce-picker__empty">
          <span>{tutorialsError}</span>
          <button type="button" className="commerce-picker__text-btn" onClick={loadTutorials}>{t('commerce.retry')}</button>
        </div>
      );
    }
    if (videoTutorials.length === 0) {
      return <div className="commerce-picker__empty">{t('commerce.noVideos')}</div>;
    }

    return (
      <div className="commerce-picker__video-stage">
        <div className="commerce-picker__video-player-wrap">
          {activeVideoUrl ? (
            <video
              key={activeVideoUrl}
              ref={videoPlayerRef}
              className="commerce-picker__video-player"
              src={activeVideoUrl}
              controls
              playsInline
              preload="metadata"
              poster={activeVideo?.coverUrl || undefined}
              onLoadedData={handleVideoReady}
            >
              {t('commerce.videoUnsupported')}
            </video>
          ) : (
            <div className="commerce-picker__empty">{t('commerce.noVideoUrl')}</div>
          )}
          {activeVideo ? (
            <div className="commerce-picker__video-now">
              <strong>{activeVideo.title || t('commerce.untitled')}</strong>
              {activeVideo.description ? <span>{activeVideo.description}</span> : null}
            </div>
          ) : null}
        </div>

        {videoTutorials.length > 1 ? (
          <div className="commerce-picker__tutorial-grid commerce-picker__tutorial-grid--videos" role="listbox" aria-label={t('commerce.pickVideo')}>
            {videoTutorials.map((item) => {
              const selected = item.id === activeVideoId;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`commerce-picker__tutorial-card${selected ? ' is-active' : ''}`}
                  onClick={() => selectVideo(item)}
                >
                  <div
                    className={`commerce-picker__tutorial-cover${item.coverUrl ? '' : ' commerce-picker__tutorial-cover--file commerce-picker__tutorial-cover--video'}`}
                    style={item.coverUrl ? { backgroundImage: `url(${item.coverUrl})` } : undefined}
                  >
                    <span className="commerce-picker__play-badge" aria-hidden="true">
                      <Play size={18} fill="currentColor" />
                    </span>
                    <span>{t('commerce.video')}</span>
                  </div>
                  <div className="commerce-picker__tutorial-body">
                    <strong>{item.title || t('commerce.untitled')}</strong>
                    {item.description ? <span>{item.description}</span> : null}
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      ref={pageRef}
      className={`commerce-picker${embedded ? ' commerce-picker--embedded' : ''}`}
    >
      <div className="commerce-picker__bg" aria-hidden="true">
        <div
          className="commerce-picker__bg-pane commerce-picker__bg-pane--shein"
          style={{ backgroundImage: `url(${SHEIN_CARD_BG})` }}
        />
        <div
          className="commerce-picker__bg-pane commerce-picker__bg-pane--temu"
          style={{ backgroundImage: `url(${TEMU_CARD_BG})` }}
        />
        <div
          className="commerce-picker__bg-pane commerce-picker__bg-pane--tiktok"
          style={{ backgroundImage: `url(${TIKTOK_CARD_BG})` }}
        />
      </div>

      <div className="commerce-picker__scroll">
        <section className="commerce-picker__hero" aria-label={t('commerce.heroAria')}>
          <div className="commerce-picker__hero-inner">
            <p className="commerce-picker__brand">IPE</p>
            <h1>{t('commerce.heroTitle')}</h1>
            <p className="commerce-picker__hero-lead">
              {t('commerce.heroLead')}
            </p>
            <div className="commerce-picker__hero-cta">
              <button
                type="button"
                className="commerce-picker__btn commerce-picker__btn--primary"
                onClick={() => scrollTo(videosRef)}
              >
                <Film size={16} aria-hidden />
                {t('commerce.watchTutorials')}
              </button>
              <button
                type="button"
                className="commerce-picker__btn commerce-picker__btn--ghost"
                onClick={() => scrollTo(platformsRef)}
              >
                {t('commerce.enterWorkspace')}
              </button>
            </div>
            <button
              type="button"
              className="commerce-picker__scroll-hint"
              onClick={() => scrollTo(featuresRef)}
              aria-label={t('commerce.scrollMore')}
            >
              <ArrowDown size={18} aria-hidden />
            </button>
          </div>
        </section>

        <section
          ref={featuresRef}
          className="commerce-picker__section commerce-picker__features"
          aria-label={t('commerce.featuresAria')}
        >
          <div className="commerce-picker__section-inner" data-reveal>

            <p className="commerce-picker__section-lead">
              {t('commerce.featuresLead')}
            </p>
            <div className="commerce-picker__feature-list">
              {FEATURES.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <article
                    key={feature.id}
                    className={`commerce-picker__feature${index % 2 === 1 ? ' commerce-picker__feature--flip' : ''}`}
                    data-reveal
                  >
                    <div className="commerce-picker__feature-icon" aria-hidden="true">
                      <Icon size={28} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3>{t(`commerce.${feature.titleKey}`)}</h3>
                      <p>{t(`commerce.${feature.descKey}`)}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section
          ref={platformsRef}
          className="commerce-picker__section commerce-picker__platforms"
          aria-label={t('commerce.platformsAria')}
        >
          <div className="commerce-picker__section-inner" data-reveal>
            <p className="commerce-picker__eyebrow">{t('commerce.platformEyebrow')}</p>
            <h2>{t('commerce.chooseTitle')}</h2>
            <p className="commerce-picker__section-lead">
              {embedded ? t('commerce.embeddedHint') : t('commerce.standaloneHint')}
            </p>
            <div className="commerce-picker__grid">
              <button
                type="button"
                className="commerce-picker__card commerce-picker__card--shein"
                style={{ '--cp-card-image': `url(${SHEIN_CARD_BG})` }}
                onClick={() => openGate(IPE_SHEIN_GATE_PATH)}
              >
                <strong>SHEIN</strong>
                <span>{t('commerce.sheinCard')}</span>
              </button>
              <button
                type="button"
                className={`commerce-picker__card commerce-picker__card--temu${!TEMU_WORKSPACE_ENABLED ? ' is-disabled' : ''}`}
                style={{ '--cp-card-image': `url(${TEMU_CARD_BG})` }}
                onClick={openTemuGate}
                aria-disabled={!TEMU_WORKSPACE_ENABLED}
              >
                {!TEMU_WORKSPACE_ENABLED ? (
                  <span className="commerce-picker__dev-badge">{t('commerce.inDevelopment')}</span>
                ) : null}
                <strong>TEMU</strong>
                <span>
                  {TEMU_WORKSPACE_ENABLED ? t('commerce.temuCard') : t('commerce.comingSoon')}
                </span>
              </button>
              <button
                type="button"
                className={`commerce-picker__card commerce-picker__card--tiktok${!TIKTOK_WORKSPACE_ENABLED ? ' is-disabled' : ''}`}
                style={{ '--cp-card-image': `url(${TIKTOK_CARD_BG})` }}
                onClick={openTiktokGate}
                aria-disabled={!TIKTOK_WORKSPACE_ENABLED}
              >
                {!TIKTOK_WORKSPACE_ENABLED ? (
                  <span className="commerce-picker__dev-badge commerce-picker__dev-badge--tiktok">{t('commerce.inDevelopment')}</span>
                ) : null}
                <strong>TikTok</strong>
                <span>
                  {TIKTOK_WORKSPACE_ENABLED ? t('commerce.tiktokCard') : t('commerce.comingSoon')}
                </span>
              </button>
            </div>
          </div>
        </section>

        <section
          ref={videosRef}
          className="commerce-picker__section commerce-picker__tutorials"
          aria-label={t('commerce.videosAria')}
        >
          <div className="commerce-picker__section-inner" data-reveal>
            <p className="commerce-picker__eyebrow">
              <Film size={14} aria-hidden />
              {t('commerce.videosEyebrow')}
            </p>
            <h2>{t('commerce.videosTitle')}</h2>
            <p className="commerce-picker__section-lead">
              {t('commerce.videosLead')}
            </p>
            {renderVideoSectionBody()}
            <button
              type="button"
              className="commerce-picker__text-btn commerce-picker__text-btn--docs"
              onClick={() => scrollTo(docsRef)}
            >
              <BookOpen size={15} aria-hidden />
              {t('commerce.viewDocs')}
            </button>
          </div>
        </section>

        <section
          ref={docsRef}
          className="commerce-picker__section commerce-picker__tutorials commerce-picker__tutorials--docs"
          aria-label={t('commerce.docsAria')}
        >
          <div className="commerce-picker__section-inner" data-reveal>
            <p className="commerce-picker__eyebrow">
              <FileText size={14} aria-hidden />
              {t('commerce.docsEyebrow')}
            </p>
            <h2>{t('commerce.docsTitle')}</h2>
            <p className="commerce-picker__section-lead">
              {t('commerce.docsLead')}
            </p>
            {renderDocumentGrid(documentTutorials, t('commerce.noDocs'))}
          </div>
        </section>

        <section className="commerce-picker__section commerce-picker__footer-cta" aria-label={t('commerce.startAria')}>
          <div className="commerce-picker__section-inner" data-reveal>
            <h2>{t('commerce.startTitle')}</h2>
            <p className="commerce-picker__section-lead">
              {t('commerce.startLead')}
            </p>
            <div className="commerce-picker__hero-cta">
              <button
                type="button"
                className="commerce-picker__btn commerce-picker__btn--shein"
                onClick={() => openGate(IPE_SHEIN_GATE_PATH)}
              >
                {t('commerce.enterShein')}
              </button>
              <button
                type="button"
                className={`commerce-picker__btn commerce-picker__btn--temu${!TEMU_WORKSPACE_ENABLED ? ' is-disabled' : ''}`}
                onClick={openTemuGate}
                aria-disabled={!TEMU_WORKSPACE_ENABLED}
              >
                {TEMU_WORKSPACE_ENABLED ? t('commerce.enterTemu') : t('commerce.temuDev')}
              </button>
              <button
                type="button"
                className={`commerce-picker__btn commerce-picker__btn--tiktok${!TIKTOK_WORKSPACE_ENABLED ? ' is-disabled' : ''}`}
                onClick={openTiktokGate}
                aria-disabled={!TIKTOK_WORKSPACE_ENABLED}
              >
                {TIKTOK_WORKSPACE_ENABLED ? t('commerce.enterTiktok') : t('commerce.tiktokDev')}
              </button>
            </div>
          </div>
        </section>
      </div>

      <PlatformTutorialPreviewModal
        open={Boolean(viewer)}
        title={viewer?.title}
        url={viewer?.url}
        type={viewer?.type}
        mimeType={viewer?.mimeType}
        fileName={viewer?.fileName}
        onClose={() => setViewer(null)}
      />
    </div>
  );
}
